import React, { useCallback, useEffect, useState } from "react";
import { timeLogsAPI } from "../../api/client";
import { formatDuration, formatDurationSeconds } from "../../utils/formatDuration";

const SUBTLE = { fontSize: 12 };
const ERROR_COLOR = "#b91c1c";

// Time logged after a project is COMPLETED. The server decides eligibility
// (completed project + the user finished a stage on it); this panel only
// renders what it is told and never shows for users who can't log.
export default function AdditionalWorkPanel({ projectId }) {
  const [state, setState] = useState(null);
  const [reason, setReason] = useState("");
  // Kept apart from `error`: this one belongs under the remark input that
  // caused it, while `error` is a server/network failure shown at the foot.
  const [reasonError, setReasonError] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(async () => {
    try {
      const resp = await timeLogsAPI.additionalWorkState(projectId);
      setState(resp.data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load additional work");
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const active = state?.active || null;

  useEffect(() => {
    if (!active) return undefined;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [active]);

  const start = async () => {
    // The button stays enabled on purpose: a disabled button gives no hint why
    // nothing happens, so an empty remark is explained here instead.
    if (!reason.trim()) {
      setReasonError("Add a short remark describing the work before starting.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await timeLogsAPI.startAdditionalWork(projectId, reason.trim());
      setReason("");
      await load();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to start timer");
    } finally {
      setBusy(false);
    }
  };

  const pause = async () => {
    setBusy(true);
    setError("");
    try {
      await timeLogsAPI.pause(active.stageAssignmentId);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to pause timer");
    } finally {
      setBusy(false);
    }
  };

  if (!state) {
    return error ? (
      <div className="small-muted" style={SUBTLE}>
        {error}
      </div>
    ) : null;
  }
  if (!state.eligible && state.sessions.length === 0) return null;

  const sessionSeconds = active
    ? Math.max(0, Math.round((now - new Date(active.startedAt).getTime()) / 1000))
    : 0;
  const closedSessions = state.sessions.filter((s) => s.endedAt);

  return (
    <div className="panel" style={{ marginBottom: 12, padding: "10px 14px" }}>
      <div
        className="stage-assign-row"
        style={{ justifyContent: "space-between", alignItems: "baseline" }}
      >
        <span className="small-muted" style={{ fontWeight: 600, fontSize: 13 }}>
          Additional work
        </span>
        <span className="small-muted" style={SUBTLE}>
          Your total: {formatDuration(state.totalMinutes)}
        </span>
      </div>
      <p className="small-muted" style={{ ...SUBTLE, margin: "2px 0 8px" }}>
        This project is completed. Time logged here is recorded as additional
        work and appears in all reports.
      </p>

      {state.eligible && !active && (
        <div>
          <div className="stage-assign-row">
            <input
              type="text"
              style={{
                flex: 1,
                minWidth: 200,
                fontSize: 13,
                ...(reasonError ? { borderColor: ERROR_COLOR } : {}),
              }}
              placeholder="Remark: what are you working on?"
              value={reason}
              aria-invalid={!!reasonError}
              onChange={(e) => {
                setReason(e.target.value);
                if (reasonError) setReasonError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !busy) start();
              }}
              maxLength={300}
            />
            <button
              className="btn-secondary"
              style={{ fontSize: 13 }}
              disabled={busy}
              onClick={start}
            >
              Start timer
            </button>
          </div>
          {reasonError && (
            <div style={{ ...SUBTLE, color: ERROR_COLOR, marginTop: 4 }}>
              {reasonError}
            </div>
          )}
        </div>
      )}

      {active && (
        <div className="stage-assign-row" style={{ alignItems: "center" }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>
            {formatDurationSeconds(sessionSeconds)}
          </div>
          <div className="small-muted" style={{ ...SUBTLE, flex: 1 }}>
            {active.reason}
          </div>
          <button
            className="btn-secondary"
            style={{ fontSize: 13 }}
            disabled={busy}
            onClick={pause}
          >
            Stop
          </button>
        </div>
      )}

      {closedSessions.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {closedSessions.map((s) => (
            <div key={s.id} className="small-muted" style={SUBTLE}>
              {new Date(s.startedAt).toLocaleDateString()} ·{" "}
              {formatDuration(s.durationMinutes || 0)} — {s.reason}
            </div>
          ))}
        </div>
      )}

      {error && (
        <div style={{ ...SUBTLE, color: ERROR_COLOR, marginTop: 8 }}>
          {error}
        </div>
      )}
    </div>
  );
}
